import { useTextField } from '@react-aria/textfield';
import { useId } from '@react-aria/utils';
import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';

import { Eye, EyeClosed } from '@src/components/atoms/icons/eye';
import { ErrorMessage } from '@src/components/atoms/inputs/error-message';
import { FloatingLabel } from '@src/components/atoms/inputs/floating-label';
import { InputProps } from '@src/components/atoms/inputs/input/types';
import { useInputHasValue } from '@src/hooks/use-input-has-value';

import { InputWrapper, TogglePasswordVisibility } from './styles';

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, isQuiet = false, className, ...restProps }, ref) => {
    const errorMessageId = useId();
    const inputRef = useRef<HTMLInputElement>(ref && typeof ref !== 'function' ? ref.current : null);
    const { labelProps, inputProps } = useTextField(
      {
        label,
        ...restProps,
        'aria-errormessage': error ? errorMessageId : undefined,
      },
      inputRef,
    );
    const { isActive, inputProps: focusProps } = useInputHasValue(inputProps, 'value');

    useEffect(() => {
      if (!ref || !inputRef) {
        return;
      }

      if (typeof ref === 'function') {
        ref(inputRef.current);
      }

      if (typeof ref === 'object') {
        // eslint-disable-next-line no-param-reassign
        ref.current = inputRef.current;
      }
    }, [ref, inputRef]);

    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const type = restProps.type === 'password' && isPasswordVisible ? 'text' : restProps.type;

    return (
      <div className={className}>
        <InputWrapper isQuiet={isQuiet} hasError={!!error}>
          <input {...inputProps} {...focusProps} type={type} ref={inputRef} />
          {restProps.type === 'password' && (
            <TogglePasswordVisibility type="button" onClick={() => setIsPasswordVisible(!isPasswordVisible)}>
              {isPasswordVisible ? <Eye /> : <EyeClosed />}
            </TogglePasswordVisibility>
          )}

          <FloatingLabel isQuiet={isQuiet} isActive={isActive} {...labelProps}>
            {label}
          </FloatingLabel>
        </InputWrapper>

        {error && <ErrorMessage id={errorMessageId}>{error}</ErrorMessage>}
      </div>
    );
  },
);
Input.displayName = 'Input';
export default styled(Input)``;
